import {
	Box,
	Button,
	HStack,
	VStack,
	Text,
	Collapse,
	IconButton,
	Wrap,
	WrapItem,
	Tag,
	Menu,
	MenuButton,
	MenuList,
	MenuItem,
	Switch,
	SimpleGrid,
	Circle,
	Input,
	InputGroup,
	InputLeftElement,
	Divider,
	Tooltip,
	Icon,
} from "@chakra-ui/react";
import React, { useState, useEffect, useMemo } from "react";
import { FiPlus, FiTrash2, FiCopy, FiPlay, FiMoreVertical } from "react-icons/fi";
import { ChevronLeftIcon, SearchIcon } from "@chakra-ui/icons";
import ScenarioCreationComponent from "./ScenarioCreationComponent";
import { DataScopeProto, ScenarioAnalysis } from "build-ts/analyst";
import { FilterProto } from "build-ts/metadata";
import { v4 as uuidv4 } from "uuid";
import showToast from "hooks/useCustomToast";
import { getOperatorSymbol } from "utils/analysisUtils";
import { getFilterDisplayString } from "utils/nameUtils";
import ChatPage from "pages/chat/ChatPage";
import { FaArrowUp } from "react-icons/fa";


interface AnalystScenariosComponentProps {
	dataScope: DataScopeProto | undefined;
	scenarios: ScenarioAnalysis[];
	setScenarios: React.Dispatch<React.SetStateAction<ScenarioAnalysis[]>>;
	isTabActive: number;
	// States from parent to control overlay buttons
	isCreatingScenario: boolean;
	setIsCreatingScenario: React.Dispatch<React.SetStateAction<boolean>>;
	isEditingScenario: boolean;
	setIsEditingScenario: React.Dispatch<React.SetStateAction<boolean>>;
	isNavbarCollapsed: boolean;
	// Callback to save analyst when scenario is saved (accepts updated scenarios)
	onSaveAnalyst?: (updatedScenarios?: ScenarioAnalysis[]) => Promise<void>;
	/** Current analyst id (for Chat overlay so chat uses this analyst) */
	analystId?: string;
	/** Notify parent when user opens/closes Scenario Creation Chat (so parent can hide top bar and overlay) */
	onScenarioChatOpenChange?: (open: boolean) => void;
}

const AnalystScenariosComponent: React.FC<AnalystScenariosComponentProps> = ({
	dataScope,
	scenarios,
	setScenarios,
	isTabActive,
	isCreatingScenario,
	setIsCreatingScenario,
	isEditingScenario,
	setIsEditingScenario,
	isNavbarCollapsed,
	onSaveAnalyst,
	analystId,
	onScenarioChatOpenChange,
}) => {
	// For Scenario creation
	const [scenario, setScenario] = useState<ScenarioAnalysis | undefined>(undefined);
	// Bulk preset filters map: id -> FilterProto[]
	const [bulkPresetFiltersMap, setBulkPresetFiltersMap] = useState<Map<string, FilterProto[]>>(new Map());

	// For editing scenario
	const [editingScenarioIndex, setEditingScenarioIndex] = useState<number | null>(null);

	// Track scenario form changes
	const [hasScenarioChanges, setHasScenarioChanges] = useState<boolean>(false);

	// Search state
	const [searchTerm, setSearchTerm] = useState<string>("");

	// Chat overlay (no URL change)
	const [showChatPage, setShowChatPage] = useState<boolean>(false);
	// When opening chat from the search bar, this is the question to send as first message
	const [initialPromptForChat, setInitialPromptForChat] = useState<string | undefined>(undefined);
	// Search bar value for "create scenario" input (opens chat and sends this as initial message on submit)
	const [scenarioSearchPrompt, setScenarioSearchPrompt] = useState<string>("");
	// Selected scenario ids in chat menu: empty/undefined = "All scenarios", otherwise multi-scenario context
	const [selectedScenarioIdsForChat, setSelectedScenarioIdsForChat] = useState<string[]>([]);
	// When true, chat component stays mounted (hidden when !showChatPage) so "Chat" preserves session
	const [hasOpenedChatOnce, setHasOpenedChatOnce] = useState<boolean>(false);
	// Increment to force a new chat session (used when opening from search input)
	const [chatSessionKey, setChatSessionKey] = useState<number>(0);

	// Filter scenarios based on search term
	const filteredScenarios = useMemo(() => {
		if (!searchTerm.trim()) {
			return scenarios;
		}

		const lowerSearchTerm = searchTerm.toLowerCase();

		return scenarios.filter((scenario) => {
			// Search in issue description
			const issueMatch = scenario.issue?.toLowerCase().includes(lowerSearchTerm);

			// Search in filter display text
			let filterMatch = false;
			if (scenario.preset_filters && scenario.preset_filters.length > 0) {
				filterMatch = scenario.preset_filters.some((filter) => {
					let displayText = "";
					if (filter.date_filter) {
						displayText = `${filter.kg_column_header?.name}: ${getFilterDisplayString(filter)}`;
					} else if (filter.kg_column_header && filter.values && filter.values.length > 0) {
						const operatorSymbol = getOperatorSymbol(filter.operator!);
						displayText = `${filter.kg_column_header.name} ${operatorSymbol} ${filter.values.join(", ")}`;
					}
					return displayText.toLowerCase().includes(lowerSearchTerm);
				});
			}

			return issueMatch || filterMatch;
		});
	}, [scenarios, searchTerm]);

	useEffect(() => {
		if (isTabActive === 1) {
			setIsCreatingScenario(false);
		}
	}, [isTabActive]);

	// Save scenario handler
	const handleSaveScenario = () => {
		if (!scenario) {
			showToast("Please fill in scenario details", "", "warning");
			return;
		}

		// Validate basic fields
		if (!scenario.issue || scenario.issue.trim() === "" || !scenario.trigger || scenario.trigger.trim() === "") {
			showToast("Please provide an issue description and trigger", "", "warning");
			return;
		}

		// Track final scenarios for auto-save
		let finalScenarios: ScenarioAnalysis[] = [];

		// If editing existing scenario
		if (editingScenarioIndex !== null) {
			const updatedScenarios = [...scenarios];
			const presetFilterSets = Array.from(bulkPresetFiltersMap.values());

			if (presetFilterSets.length === 0) {
				// No preset filters - just update the scenario
				updatedScenarios[editingScenarioIndex] = {
					...scenario,
					id: scenarios[editingScenarioIndex].id,
					preset_filters: [],
				};
				setScenarios(updatedScenarios);
				finalScenarios = updatedScenarios;
				showToast("Scenario updated successfully", "", "success");
			} else if (presetFilterSets.length === 1) {
				// Single preset filter - just update the scenario
				updatedScenarios[editingScenarioIndex] = {
					...scenario,
					id: scenarios[editingScenarioIndex].id,
					preset_filters: presetFilterSets[0],
				};
				setScenarios(updatedScenarios);
				finalScenarios = updatedScenarios;
				showToast("Scenario updated successfully", "", "success");
			} else {
				// Multiple preset filters - update first, create new for rest
				// Update the existing scenario with the first filter set
				updatedScenarios[editingScenarioIndex] = {
					...scenario,
					id: scenarios[editingScenarioIndex].id,
					preset_filters: presetFilterSets[0],
				};

				// Create new scenarios for the remaining filter sets
				const newScenarios: ScenarioAnalysis[] = [];
				for (let i = 1; i < presetFilterSets.length; i++) {
					newScenarios.push({
						...scenario,
						id: uuidv4(),
						preset_filters: presetFilterSets[i],
					});
				}

				// Combine updated and new scenarios
				finalScenarios = [...updatedScenarios, ...newScenarios];
				setScenarios(finalScenarios);
				showToast(`Scenario updated and ${newScenarios.length} new scenario(s) created`, "", "success");
				setIsEditingScenario(false);
			}
		} else {
			// Creating new scenarios
			const newScenarios: ScenarioAnalysis[] = [];

			// If no preset filters, create one scenario without filters
			if (bulkPresetFiltersMap.size === 0) {
				const newScenario: ScenarioAnalysis = {
					...scenario,
					id: uuidv4(),
					preset_filters: [],
				};
				newScenarios.push(newScenario);
			} else {
				// Create one scenario for each preset filter set
				bulkPresetFiltersMap.forEach((filters, filterId) => {
					const newScenario: ScenarioAnalysis = {
						...scenario,
						id: uuidv4(),
						preset_filters: filters,
					};
					newScenarios.push(newScenario);
				});
			}

			// Add new scenarios to the list
			finalScenarios = [...scenarios, ...newScenarios];
			setScenarios(finalScenarios);
			showToast(`${newScenarios.length} scenario(s) created successfully`, "", "success");
		}

		// Reset form
		setIsCreatingScenario(false);
		setScenario(undefined);
		setBulkPresetFiltersMap(new Map());
		setEditingScenarioIndex(null);
		setIsEditingScenario(false);

		// Auto-save analyst with updated scenarios
		onSaveAnalyst?.(finalScenarios);
	};

	// Delete scenario handler
	const handleDeleteScenario = (index: number) => {
		const updatedScenarios = scenarios.filter((_, i) => i !== index);
		setScenarios(updatedScenarios);
		showToast("Scenario deleted successfully", "", "success");
	};

	// Edit scenario handler
	const handleEditScenario = (index: number) => {
		const scenarioToEdit = scenarios[index];
		setEditingScenarioIndex(index);
		setIsEditingScenario(true);
		setScenario(scenarioToEdit);

		// Set preset filters if available
		if (scenarioToEdit.preset_filters && scenarioToEdit.preset_filters.length > 0) {
			// Populate bulkPresetFiltersMap with existing filter
			const newMap = new Map<string, FilterProto[]>();
			newMap.set("1", scenarioToEdit.preset_filters);
			setBulkPresetFiltersMap(newMap);
		} else {
			setBulkPresetFiltersMap(new Map());
		}

		setIsCreatingScenario(true);
	};

	// Toggle disable state handler
	const handleToggleDisable = (index: number) => {
		setScenarios((prevScenarios) => {
			const updatedScenarios = [...prevScenarios];
			updatedScenarios[index] = {
				...updatedScenarios[index],
				disable: !updatedScenarios[index].disable,
			};
			return updatedScenarios;
		});
	};

	// Copy scenario handler
	const handleCopyScenario = (index: number) => {
		const scenarioToCopy = scenarios[index];
		// Create a copy without the ID so a new ID will be generated on save
		const copiedScenario = {
			...scenarioToCopy,
			id: undefined, // Remove ID to create a new scenario
		};

		setEditingScenarioIndex(null); // Not editing, creating a new one
		setIsEditingScenario(false);
		setScenario(copiedScenario);

		// Copy preset filters if available
		if (scenarioToCopy.preset_filters && scenarioToCopy.preset_filters.length > 0) {
			const newMap = new Map<string, FilterProto[]>();
			newMap.set("1", scenarioToCopy.preset_filters);
			setBulkPresetFiltersMap(newMap);
		} else {
			setBulkPresetFiltersMap(new Map());
		}

		setIsCreatingScenario(true);
		showToast("Scenario copied", "Modify and save to create a new scenario", "info");
	};

	// Run scenario handler
	const handleRunScenario = (index: number) => {
		showToast("Run Scenario", "This feature has not been implemented yet", "info");
	};

	// Chat stays mounted when hasOpenedChatOnce so "Chat" preserves session; key forces new session when opening from search
	const chatPage = hasOpenedChatOnce ? (
		<Box display={showChatPage ? "block" : "none"} height="100%" width="100%">
			<ChatPage
				key={chatSessionKey}
				initialAnalystId={analystId}
				selectedScenarioIds={selectedScenarioIdsForChat}
				onScenarioIdsChange={(scenarioIds) => setSelectedScenarioIdsForChat(scenarioIds ?? [])}
				onBackToAnalystScenarios={() => {
					onScenarioChatOpenChange?.(false);
					setShowChatPage(false);
					setInitialPromptForChat(undefined);
				}}
				initialPrompt={initialPromptForChat}
			/>
		</Box>
	) : null;

	return (
		<>
		<Box
			display={showChatPage ? "none" : "block"}
			mx="auto"
			maxH="90vh"
			overflowY="auto"
			paddingBottom="120px"
			sx={{
				scrollBehavior: "smooth",
				"&::-webkit-scrollbar": {
					display: "none"
				},
				msOverflowStyle: "none",
				scrollbarWidth: "none"
			}}
		>
			{/* Header - Conditional based on creating/editing state */}
			{(isEditingScenario || isCreatingScenario) ? (
				<HStack justify="start" align="center" p={3} pb={2}>
					<Button
						size="sm"
						variant="outline"
						colorScheme="gray"
						leftIcon={<ChevronLeftIcon boxSize={5} />}
						onClick={() => {
							setIsCreatingScenario(false);
							setScenario(undefined);
							setBulkPresetFiltersMap(new Map());
							setEditingScenarioIndex(null);
							setIsEditingScenario(false);
							setHasScenarioChanges(false);
						}}
					>
						All Analysis
					</Button>
				</HStack>
			) : (
				<VStack align="stretch">
					<VStack spacing={3} align="stretch" p={4} pb={2}>
						<HStack justify="space-between" align="center">
							<Text fontSize="lg" fontWeight="700" color="fg.default" m={0}>
								Analyses ({filteredScenarios.length})
							</Text>
							{/* Search Bar */}
							<InputGroup size="md" maxW="800px">
								<InputLeftElement pointerEvents="none" height="full">
									<SearchIcon color="gray.400" boxSize={4} />
								</InputLeftElement>
								<Input
									placeholder="Search scenarios..."
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									bg="gray.50"
									border="1px"
									borderColor="gray.200"
									fontSize="sm"
									_hover={{
										borderColor: "gray.300",
										bg: "white"
									}}
									_focus={{
										borderColor: "purple.400",
										boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
										bg: "white"
									}}
									_placeholder={{
										color: "gray.400"
									}}
								/>
							</InputGroup>
							<HStack>
								<Button
									size="md"
									colorScheme="purple"
									bg="purple.600"
									color="white"
									_hover={{ bg: "purple.500" }}
									leftIcon={<FiPlus />}
									onClick={() => {
										setIsCreatingScenario(true);
										setScenario(undefined);
										setBulkPresetFiltersMap(new Map());
										setEditingScenarioIndex(null);
										setIsEditingScenario(false);
									}}
									fontWeight="700"
								>
									New Analysis
								</Button>
								<Button
									size="md"
									colorScheme="purple"
									bg="purple.600"
									color="white"
									_hover={{ bg: "purple.500" }}
									_active={{ bg: "purple.700" }}
									onClick={() => {
										// Just show chat; keep existing session if any
										setHasOpenedChatOnce(true);
										setShowChatPage(true);
										onScenarioChatOpenChange?.(true);
									}}
									fontWeight="700"
								>
									Chat
								</Button>
							</HStack>
						</HStack>
					</VStack>
				</VStack>
			)}

			{/* Scenario Creation Component */}
			<Collapse in={isCreatingScenario} animateOpacity>
				<Box p={4} pt={2}>
					<ScenarioCreationComponent
						isCreatingScenario={isCreatingScenario}
						dataScope={dataScope}
						// For bulk creation of scenarios
						scenario={scenario}
						setScenario={setScenario}
						bulkPresetFiltersMap={bulkPresetFiltersMap}
						setBulkPresetFiltersMap={setBulkPresetFiltersMap}
						// For saving scenario
						handleSaveScenario={handleSaveScenario}
						// For editing scenario
						isEditingScenario={isEditingScenario}
						isTabActive={isTabActive}
						// For change tracking
						hasScenarioChanges={hasScenarioChanges}
						setHasScenarioChanges={setHasScenarioChanges}
					/>
				</Box>
			</Collapse>

			{/* Existing Scenarios List */}
			{!isCreatingScenario && (
				<>
					<Box p={4}>
						{scenarios.length === 0 ? (
							<Text fontSize="sm" color="gray.600" textAlign="center" py={8}>
								No scenarios created yet. Click "Create New" to add a scenario.
							</Text>
						) : filteredScenarios.length === 0 ? (
							<Text fontSize="sm" color="gray.600" textAlign="center" py={8}>
								No scenarios match your search. Try different keywords.
							</Text>
						) : (
							<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
								{filteredScenarios.map((scenario, index) => {
									// Find the original index for edit/delete operations
									const originalIndex = scenarios.findIndex(s => s.id === scenario.id);
									return (
										<Box
											key={scenario.id || originalIndex}
											borderWidth="1px"
											borderRadius="md"
											p={3}
											bg="white"
											boxShadow="sm"
											_hover={{ boxShadow: "md", bg: "purple.50", cursor: "pointer" }}
											transition="all 0.2s"
											onClick={() => handleEditScenario(originalIndex)}
										>
											<HStack justify="space-between" align="center">
												<VStack align="flex-start" flex="1" spacing={2}>
													<HStack spacing={3} w="100%">
														<Circle
															size="24px"
															bg="purple.600"
															color="white"
															fontWeight="bold"
															fontSize="sm"
															flexShrink={0}
														>
															{originalIndex + 1}
														</Circle>
														<HStack spacing={2} justify="space-between" width="100%">
															<Text fontSize="sm" color="gray.700" fontWeight="700" textAlign="left" m={0}>
																{scenario.issue || "No issue description"}
															</Text>
															<Tooltip
																label={scenario.disable ? "Click to enable" : "Click to disable"}
																placement="top"
																hasArrow
															>
																<Box
																	as="span"
																	display="inline-block"
																	onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
																>
																	<Switch
																		size="sm"
																		colorScheme="green"
																		isChecked={!scenario.disable}
																		m={0}
																		onChange={(e) => handleToggleDisable(originalIndex)}
																	/>
																</Box>
															</Tooltip>
														</HStack>
													</HStack>

													<Divider my={0} borderColor="gray.500" mb={1} />

													{/* Preset Filters Display */}
													{scenario.preset_filters && scenario.preset_filters.length > 0 && (
														<HStack w="100%" textAlign="left">
															<Wrap
																spacing={1}
																justify="flex-start"
																mb={0}
																gap={0}
																css={{
																	// Remove default list spacing from Chakra Wrap (Wrap renders a <ul> tag)
																	lineHeight: 0,
																	"& > ul": {
																		margin: 0,
																		padding: 0,
																	}
																}}
															>
																{scenario.preset_filters.map((filter, filterIndex) => {
																	let displayText = "";
																	if (filter.date_filter) {
																		displayText = `${filter.kg_column_header?.name}: ${getFilterDisplayString(filter)}`;
																	} else if (filter.kg_column_header && filter.values && filter.values.length > 0) {
																		const operatorSymbol = getOperatorSymbol(filter.operator!);
																		displayText = `${filter.kg_column_header.name} ${operatorSymbol} ${filter.values.join(", ")}`;
																	}

																	return displayText ? (
																		<WrapItem key={filterIndex}>
																			<Tag
																				size="sm"
																				colorScheme="purple"
																				fontSize="xs"
																				bg="purple.50"
																				border="1px"
																				borderColor="purple.300"
																				color="gray.800"
																				py={1}
																			>
																				{displayText}
																			</Tag>
																		</WrapItem>
																	) : null;
																})}
															</Wrap>
														</HStack>
													)}
												</VStack>
												<Box onClick={(e) => e.stopPropagation()} >
													<Menu>
														<MenuButton
															as={IconButton}
															icon={<FiMoreVertical />}
															variant="ghost"
															size="sm"
															aria-label="Scenario options"
															_hover={{ bg: "gray.100" }}
														/>
														<MenuList minW="160px" borderRadius="lg">
															<MenuItem
																icon={<FiCopy />}
																onClick={() => handleCopyScenario(originalIndex)}
																_hover={{ bg: "gray.100" }}
															>
																Copy
															</MenuItem>
															<MenuItem
																icon={<FiPlay />}
																onClick={() => handleRunScenario(originalIndex)}
																_hover={{ bg: "gray.100" }}
															>
																Run
															</MenuItem>
															<MenuItem
																icon={<FiTrash2 />}
																onClick={() => handleDeleteScenario(originalIndex)}
																color="red.600"
																_hover={{ bg: "gray.100" }}
															>
																Delete
															</MenuItem>
														</MenuList>
													</Menu>
												</Box>
											</HStack>
										</Box>
									);
								})}
							</SimpleGrid>
						)}
					</Box>
					<Box
						display={showChatPage ? "none" : "flex"}
						position="fixed"
						bottom="72px"
						left={`calc(${isNavbarCollapsed ? 50 : 250}px + 1.5rem)`}
						right="1.5rem"
						width="100%"
						maxW={`min(850px, calc(100vw - ${isNavbarCollapsed ? 50 : 250}px - 3rem))`}
						marginLeft="auto"
						marginRight="auto"
						justifyContent="center"
						zIndex={999}
						pointerEvents="none"
						boxSizing="border-box"
					>
						<Box
							pointerEvents="auto"
							w="100%"
							maxW="100%"
							minW={0}
							border="2px solid"
							borderColor="gray.300"
							borderRadius="xl"
							bg="white"
							overflow="hidden"
							_focusWithin={{ borderColor: "purple.400", boxShadow: "0 0 0 1px var(--chakra-colors-purple-600)" }}
							boxShadow="sm"
						>
							<HStack spacing={2} align="flex-end" p={2}>
								<Input
									placeholder="Type what kind of scenario you need we'll create it for you..."
									value={scenarioSearchPrompt}
									onChange={(e) => setScenarioSearchPrompt(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											const trimmed = scenarioSearchPrompt.trim();
											setSelectedScenarioIdsForChat([]); // Always "All scenarios" for new session from search
											setInitialPromptForChat(trimmed || undefined);
											setScenarioSearchPrompt("");
											setChatSessionKey((k) => k + 1); // Force new chat session
											setHasOpenedChatOnce(true);
											setShowChatPage(true);
											onScenarioChatOpenChange?.(true);
										}
									}}
									border="none"
									_focus={{ boxShadow: "none" }}
									fontSize="md"
									px={2}
									py={2}
									width="100%"
								/>
								<IconButton
									aria-label="Send and open chat"
									icon={<Icon as={FaArrowUp} boxSize={4} color="white" />}
									bg="purple.600"
									borderRadius="full"
									size="sm"
									_hover={{ bg: "purple.700" }}
									_disabled={{ bg: "gray.300", cursor: "not-allowed" }}
									flexShrink={0}
									alignSelf="center"
									onClick={() => {
										const trimmed = scenarioSearchPrompt.trim();
										setSelectedScenarioIdsForChat([]); // Always "All scenarios" for new session from search
										setInitialPromptForChat(trimmed || undefined);
										setScenarioSearchPrompt("");
										setChatSessionKey((k) => k + 1); // Force new chat session
										setHasOpenedChatOnce(true);
										setShowChatPage(true);
										onScenarioChatOpenChange?.(true);
									}}
								/>
							</HStack>
						</Box>
					</Box>
				</>
			)}
		</Box>
		{chatPage}
	</>);
};

export default AnalystScenariosComponent;
